import { useMutation } from '@apollo/client/react';
import {
  ENDPUNKTE_SPEICHERN,
  ZUGANG_ERSTELLEN,
} from '../lib/graphql/mutations.ts';
import * as Sentry from '@sentry/react';
import type { EndpunktDefinitionInput } from '../gql/graphql.ts';
import { GET_ZUGAENGE } from '../lib/graphql/queries.ts';

export type ErstellenErgebnis =
  | 'erfolg'
  | 'endpunkteFehlgeschlagen'
  | 'limitErreicht'
  | 'titelZuLang'
  | 'fehlgeschlagen';

export type ZugangErstellen = {
  erstellen: (
    titel: string,
    definitionen: EndpunktDefinitionInput[],
  ) => Promise<ErstellenErgebnis>;
  loading: boolean;
};

export function useZugangErstellen(): ZugangErstellen {
  const [zugangErstellen, { loading: zugangErstellenLaeuft }] = useMutation(
    ZUGANG_ERSTELLEN,
    {
      update(cache, { data }) {
        if (data?.erstelleZugang.__typename !== 'ZugangErstellenSuccess') {
          return;
        }

        const neuerZugang = data.erstelleZugang.zugang;

        cache.updateQuery({ query: GET_ZUGAENGE }, (queryState) => {
          if (queryState?.zugaenge.__typename !== 'ZugangQuerySuccess') {
            return;
          }

          return {
            zugaenge: {
              ...queryState.zugaenge,
              zugaenge: [...queryState.zugaenge.zugaenge, neuerZugang],
            },
          };
        });
      },
    },
  );

  const [endpunkteSpeichern, { loading: endpunkteSpeichernLaeuft }] =
    useMutation(ENDPUNKTE_SPEICHERN);

  async function erstellen(
    titel: string,
    definitionen: EndpunktDefinitionInput[],
  ): Promise<ErstellenErgebnis> {
    let uidZugang: string;

    try {
      const { data } = await zugangErstellen({
        variables: { input: { beschreibung: titel } },
      });

      const ergebnis = data?.erstelleZugang;

      if (ergebnis?.__typename === 'ZugangErstellenLimitReached') {
        return 'limitErreicht';
      }
      if (ergebnis?.__typename === 'ZugangErstellenBeschreibungTooLong') {
        return 'titelZuLang';
      }
      if (ergebnis?.__typename !== 'ZugangErstellenSuccess') {
        return 'fehlgeschlagen';
      }

      uidZugang = ergebnis.zugang.uid;
    } catch (error) {
      Sentry.captureException(error);
      return 'fehlgeschlagen';
    }

    try {
      const { data } = await endpunkteSpeichern({
        variables: { zugangUid: uidZugang, endpunktDefinitionen: definitionen },
        update(cache, { data }) {
          if (
            data?.saveEndpunktDefinitionen.__typename !==
            'SaveEndpunktDefinitionSuccess'
          ) {
            return;
          }
          cache.modify({
            id: cache.identify({ __typename: 'Zugang', uid: uidZugang }),
            fields: {
              endpunktDefinitionen: () =>
                definitionen.map((definition) => ({
                  __typename: 'EndpunktDefinition',
                  ...definition,
                })),
            },
          });
        },
      });
      if (
        data?.saveEndpunktDefinitionen.__typename !==
        'SaveEndpunktDefinitionSuccess'
      ) {
        return 'endpunkteFehlgeschlagen';
      }
    } catch (error) {
      Sentry.captureException(error);
      return 'endpunkteFehlgeschlagen';
    }

    return 'erfolg';
  }

  return {
    erstellen,
    loading: zugangErstellenLaeuft || endpunkteSpeichernLaeuft,
  };
}
